import store from '@/store';
import auth from '@/common/auth/auth';
import BADGES_COLOR_MAP from '@/data/badges-color-map';

export default {
    store,
    version: 5,
    defaultColumns: [
        {
            name: 'invoiceNumber',
            sortable: true,
            type: 'link',
            // TODO: Consider to redo grid component to provide the model instead of raw fields.
            value: item => `${item.invoiceNumber}`.padStart(6, '0'),
            to: ({fields}) => ({
                name: 'invoice-details',
                params: {
                    id: fields.id,
                },
            }),
            predicate: auth.$pages['invoice-details'].isAllowed,
            predicateOverride: {
                type: 'text',
            },
            sticky: true,
        },
        {
            header: 'Products',
            value: item => [...new Set(item.items.reduce((acc, curr) => {
                if (curr._embedded && curr._embedded.product) {
                    acc.push(curr._embedded.product.name);
                }
                return acc;
            }, []))],
            style: {truncate: true},
            expand: 'organization,items.*.product',
        },
        {
            header: 'Plans',
            value: item => [...new Set(item.items.reduce((acc, curr) => {
                if (curr._embedded && curr._embedded.plan) {
                    acc.push(curr._embedded.plan.name);
                } else {
                    acc.push(curr.description);
                }
                return acc;
            }, []))],
            style: {truncate: true},
            expand: 'organization,items.*.plan',
        },
        {
            name: 'currency',
            sortable: true,
        },
        {
            name: 'amount',
            type: 'numeric',
            approx: false,
            currency: 'currency',
            format: 'currency',
            sortable: true,
        },
        {
            header: 'Customer',
            name: 'customerId',
            sortable: true,
            style: {truncate: true},
            type: 'link',
            expand: 'customer',
            value: ({_embedded: {customer}}) => {
                const {id} = customer;
                const {
                    firstName,
                    lastName,
                    organization,
                    primaryEmailAddress,
                } = customer.primaryAddress || {};
                const fullName = `${firstName || ''} ${lastName || ''}`.trim();

                // Unlike contact, prioritize the organization name for customer.
                return `${organization || ''}`.trim()
                    || fullName
                    || primaryEmailAddress
                    || id;
            },
            to: ({fields}) => ({
                name: 'customer-details',
                params: {
                    id: fields.customerId,
                },
            }),
            predicate: auth.$pages['customer-details'].isAllowed,
            predicateOverride: {
                type: 'text',
            },
        },
        {
            header: 'Date Issued',
            name: 'issuedTime',
            type: 'datetime',
            sortable: true,
        },
        {
            header: 'Payment Date',
            name: 'paidTime',
            type: 'datetime',
            sortable: true,
        },
        {
            name: 'status',
            type: 'badge',
            map: BADGES_COLOR_MAP.invoiceStatus,
            sortable: true,
        },
    ],
    otherColumns: [
        {
            name: 'id',
            style: {truncate: true},
        },
        {
            name: 'websiteId',
            style: {truncate: true},
        },
        {
            name: 'organizationId',
            style: {truncate: true},
        },
        {
            name: 'subscriptionId',
            style: {truncate: true},
        },
        {
            name: 'subtotalAmount',
            type: 'numeric',
            approx: false,
            currency: 'currency',
            format: 'currency',
        },
        {
            name: 'discountAmount',
            type: 'numeric',
            approx: false,
            currency: 'currency',
            format: 'currency',
        },
        {
            name: 'shippingAmount',
            type: 'numeric',
            approx: false,
            currency: 'currency',
            format: 'currency',
        },
        {
            name: 'type',
            sortable: true,
        },
        {
            header: 'Delivery Address First Name',
            name: 'deliveryAddress.firstName',
        },
        {
            header: 'Delivery Address Last Name',
            name: 'deliveryAddress.lastName',
        },
        {
            header: 'Delivery Address Organization',
            name: 'deliveryAddress.organization',
        },
        {
            header: 'Delivery Address',
            name: 'deliveryAddress.address',
        },
        {
            header: 'Delivery Address2',
            name: 'deliveryAddress.address2',
        },
        {
            header: 'Delivery Address City',
            name: 'deliveryAddress.city',
        },
        {
            header: 'Delivery Address Region',
            name: 'deliveryAddress.region',
        },
        {
            header: 'Delivery Address Country',
            name: 'deliveryAddress.country',
        },
        {
            header: 'Delivery Address Postal Code',
            name: 'deliveryAddress.postalCode',
        },
        {
            header: 'Delivery Address Phone Numbers',
            name: 'deliveryAddress.phoneNumbers',
            type: 'complex',
            map: v => v.value,
        },
        {
            header: 'Delivery Address Emails',
            name: 'deliveryAddress.emails',
            type: 'complex',
            map: v => v.value,
        },
        {
            header: 'Billing Address First Name',
            name: 'billingAddress.firstName',
        },
        {
            header: 'Billing Address Last Name',
            name: 'billingAddress.lastName',
        },
        {
            header: 'Billing Address Organization',
            name: 'billingAddress.organization',
        },
        {
            header: 'Billing Address',
            name: 'billingAddress.address',
        },
        {
            header: 'Billing Address2',
            name: 'billingAddress.address2',
        },
        {
            header: 'Billing Address City',
            name: 'billingAddress.city',
        },
        {
            header: 'Billing Address Region',
            name: 'billingAddress.region',
        },
        {
            header: 'Billing Address Country',
            name: 'billingAddress.country',
        },
        {
            header: 'Billing Address Postal Code',
            name: 'billingAddress.postalCode',
        },
        {
            header: 'Billing Address Phone Numbers',
            name: 'billingAddress.phoneNumbers',
            type: 'complex',
            map: v => v.value,
        },
        {
            header: 'Billing Address Emails',
            name: 'billingAddress.emails',
            type: 'complex',
            map: v => v.value,
        },
        {
            name: 'poNumber',
            type: 'numeric',
        },
        {
            name: 'notes',
        },
        {
            name: 'autopayScheduledTime',
            type: 'datetime',
            sortable: true,
        },
        {
            name: 'autopayRetryNumber',
            type: 'numeric',
            sortable: true,
        },
        {
            name: 'delinquentCollectionPeriod',
            type: 'numeric',
            sortable: true,
        },
        {
            name: 'collectionPeriod',
            type: 'numeric',
            sortable: true,
        },
        {
            name: 'abandonedTime',
            type: 'datetime',
            sortable: true,
        },
        {
            name: 'voidedTime',
            type: 'datetime',
            sortable: true,
        },
        {
            name: 'dueTime',
            type: 'datetime',
            sortable: true,
        },
        {
            name: 'createdTime',
            type: 'datetime',
            sortable: true,
        },
        {
            name: 'updatedTime',
            type: 'datetime',
            sortable: true,
        },
    ],
};
